<template>
    <div style="background-color:black;">
      <div>
        <!-- Header Section -->
        <v-row class="pa-2">
          <v-col cols="12" class="d-flex justify-space-between align-center">
            <v-btn icon class="back-button" @click="confirmBack">
              <v-icon class="white--text">mdi-arrow-left</v-icon>
            </v-btn>
            <!-- Conditionally hide the Add Job button if the role is Salesperson -->
            <v-btn
              v-if="isTechnician || !isSalesperson"
              color="#ff3b47"
              width="150px"
              rounded
              dense
              class="add-job-button my-6"
              dark
              @click="handleAddJobClick"
            >
              Add Job
            </v-btn>
          </v-col>
        </v-row>
  
        <!-- Loader while fetching data -->
        <v-row v-if="loading" justify="center" align="center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-row>
  
        <!-- Jobs Section -->
        <div v-if="!loading" style="background-color:white; border-radius:20px 20px 0px 0px; padding: 16px;">
          <!-- Search and Filter -->
          <v-row justify="center">
            <v-col cols="12" md="4">
              <v-text-field
                class="pa-3 white-search-field"
                filled
                dense
                rounded
                v-model="search"
                append-icon="mdi-magnify"
                label="Search Jobs"
                single-line
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedStatus"
                :items="statusOptions"
                placeholder="Filter by Status"
                filled
                dense
                rounded
                class="my-3"
              ></v-select>
            </v-col>
          </v-row>
  
          <!-- Jobs Cards -->
          <v-row>
            <v-col
              cols="12"
              sm="6"
              md="4"
              lg="3"
              v-for="(item, index) in paginatedItems"
              :key="index"
              class="mb-4"
            >
              <v-card class="modern-card" elevation="3" hover @click="handleCardClick(item)">
                <!-- Static Background with Job Number Overlay -->
                <div class="modern-card-background">
                  <v-row class="fill-height align-end justify-center">
                    <span class="modern-card-number">Job Number: {{ item.jobNumber }}</span>
                  </v-row>
                </div>
                <!-- Display Status in a div -->
                <v-row justify="center" align="center" class="modern-card-status">
                  <div>{{ item.status }}</div>
                </v-row>
                <!-- Customer Name Display -->
                <v-row justify="center" class="modern-card-customer-name">
                  {{ item.customerName || 'Customer Name Unavailable' }}
                </v-row>
  
                <!-- View Job Button -->
                <v-card-actions>
                  <v-btn
                    width="100%"
                    rounded
                    dark
                    color="#ff3b47"
                    @click.stop="navigateTo(item)"
                  >
                    View Job
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
  
          <!-- Load More Button -->
          <v-row justify="center" v-if="canLoadMore">
            <v-btn
              color="#ff3b47"
              @click="loadMore"
              :disabled="!canLoadMore"
              class="load-more-button"
            >
              Load More
            </v-btn>
          </v-row>
        </div>
      </div>
  
      <!-- Confirmation Dialog for Back Button -->
      <v-dialog v-model="backDialog" persistent max-width="400px">
        <v-card>
          <v-card-title class="text-h5">Confirm Navigation</v-card-title>
          <v-card-text>Are you sure you want to go back?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="backDialog = false">Cancel</v-btn>
            <v-btn color="blue darken-1" text @click="navigateToFunctions">Yes, Go Back</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  import {
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
  } from "firebase/firestore";
  import { getAuth } from "firebase/auth";
  import { firebaseApp } from "@/firebaseConfig";
  
  const auth = getAuth(firebaseApp);
  const db = getFirestore(firebaseApp);
  
  export default {
    data() {
      return {
        backDialog: false,
        items: [],
        search: "",
        selectedStatus: "", // Changed from "Job Order" to ""
        statusOptions: [
          "Inspection",
          "Costing Complete",
          "Delayed",
          "Inprogress",
          "Ready",
        ],
        userRole: "",
        userID: "",
        customers: [],
        visibleCount: 8, // Initial number of visible items
        loadIncrement: 8, // Number of items to load each time
        loading: true, // Added to manage loading state
      };
    },
    computed: {
      isSalesperson() {
        return localStorage.getItem("role") === "Salesperson";
      },
      isTechnician() {
        return localStorage.getItem("role") === "Technician";
      },
      filteredItems() {
        let filtered = this.items;
  
        if (this.search) {
          const searchLower = this.search.toLowerCase();
          filtered = filtered.filter(
            (item) =>
              (item.jobName && item.jobName.toLowerCase().includes(searchLower)) ||
              (item.jobNumber &&
                item.jobNumber.toString().toLowerCase().includes(searchLower)) ||
              (item.customerName &&
                item.customerName.toLowerCase().includes(searchLower)) ||
              (item.sealType && item.sealType.toLowerCase().includes(searchLower))
          );
        }
  
        if (this.selectedStatus) {
          filtered = filtered.filter((item) => item.status === this.selectedStatus);
        }
  
        // Sort the filtered items by jobNumber from largest to smallest
        filtered.sort((a, b) => b.jobNumber - a.jobNumber);
  
        return filtered;
      },
      paginatedItems() {
        return this.filteredItems.slice(0, this.visibleCount);
      },
      canLoadMore() {
        return this.visibleCount < this.filteredItems.length;
      },
    },
    watch: {
      search() {
        this.resetPagination();
      },
      selectedStatus() {
        this.resetPagination();
      },
    },
    async created() {
      await this.fetchUserRole();
      await this.fetchCustomers();
      await this.fetchJobs();
      this.loading = false; // Set loading to false after fetching jobs
    },
    methods: {
      confirmBack() {
        this.backDialog = true;
      },
      async fetchUserRole() {
        try {
          const user = auth.currentUser;
          if (user) {
            const uid = user.uid;
            this.userID = uid;
            const userDoc = await getDoc(doc(db, "users", uid));
            if (userDoc.exists()) {
              this.userRole = userDoc.data().role || "";
              localStorage.setItem("role", this.userRole); // Ensure 'role' is stored in localStorage
            } else {
              console.warn("No user document found!");
            }
          } else {
            console.warn("No user is currently logged in.");
          }
        } catch (error) {
          console.error("Error fetching user role:", error);
        }
      },
      async fetchCustomers() {
        try {
          const customersSnapshot = await getDocs(collection(db, "customers"));
          this.customers = customersSnapshot.docs.map((doc) => ({
            docId: doc.id,
            companyName: doc.data().companyName || "Unknown Company",
            contactPerson: doc.data().contactPerson || "Unknown Contact",
          }));
        } catch (error) {
          console.error("Error fetching customers:", error);
        }
      },
      async fetchJobs() {
        try {
          const user = auth.currentUser;
          if (user) {
            const userID = user.uid;
            let jobsQuery;
  
            // Define the statuses you want to include
            const allowedStatuses = [
              "Inspection",
              "Costing Complete",
              "Delayed",
              "Inprogress",
              "Ready",
            ];
  
            // Fetch jobs based on the user's role and allowed statuses
            if (this.userRole === "Engineer") {
              jobsQuery = query(
                collection(db, "jobs"),
                where("engineer.userID", "==", userID),
                where("status", "in", allowedStatuses)
              );
            } else if (this.userRole === "Technician") {
              jobsQuery = query(
                collection(db, "jobs"),
                where("technician.userID", "==", userID),
                where("status", "in", allowedStatuses)
              );
            } else if (this.userRole === "Salesperson") {
              jobsQuery = query(
                collection(db, "jobs"),
                where("salesperson.userID", "==", userID),
                where("status", "in", allowedStatuses)
              );
            } else {
              console.warn("User role is not Engineer, Technician, or Salesperson.");
              return;
            }
  
            const querySnapshot = await getDocs(jobsQuery);
            const jobs = [];
            querySnapshot.forEach((doc) => {
              const data = doc.data();
              jobs.push({
                id: doc.id,
                jobName: data.jobName || "Unnamed Job",
                jobNumber: data.jobNumber || "",
                status: data.status || "Quote",
                customerName: this.getCustomerName(data.customerName) || "Unknown Customer",
                sealType: data.sealType || "Unknown Seal Type",
                grandTotal: data.grandTotal || "0.00",
                comment: data.comment || "",
              });
            });
  
            // Sort jobs by jobNumber from largest to smallest
            jobs.sort((a, b) => b.jobNumber - a.jobNumber);
  
            this.items = jobs;
          } else {
            console.warn("No user is currently logged in.");
          }
        } catch (error) {
          console.error("Error fetching jobs:", error);
        }
      },
      getCustomerName(customerName) {
        // Assuming customerName is already the name, return it directly
        // If it's an ID, uncomment the following lines and adjust accordingly
        /*
        const customer = this.customers.find((cust) => cust.docId === customerId);
        return customer
          ? customer.companyName || customer.contactPerson || "Customer Name Unavailable"
          : "Customer Name Unavailable";
        */
        return customerName;
      },
      navigateToFunctions() {
        this.backDialog = false;
        this.$router.push("/Functions");
      },
      navigateTo(item) {
        if (item.id) {
          localStorage.setItem("selectedJob", item.id);
          const userRole = this.userRole;
  
          // Route based on user role
          if (userRole === "Salesperson") {
            this.$router.push("/SalesVB");
          } else if (userRole === "Technician") {
            this.$router.push("/TechVB");
          } else if (userRole === "Engineer") {
            this.$router.push("/ViewJob");
          } else {
            // Default fallback, if no specific role is found
            this.$router.push({ name: "ViewJob" });
          }
        } else {
          console.warn("Job ID is missing. Cannot navigate.");
        }
      },
      handleAddJobClick() {
        // Route based on user role
        if (this.isTechnician) {
          this.$router.push("/TechAddJob");
        } else {
          this.$router.push("/addJob");
        }
      },
      loadMore() {
        this.visibleCount += this.loadIncrement;
      },
      resetPagination() {
        this.visibleCount = this.loadIncrement;
      },
    },
  };
  </script>
  
  <style scoped>
  @import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");
  
  body,
  div,
  span,
  v-card-title,
  v-card-subtitle,
  v-card-text,
  v-row,
  v-col,
  v-card {
    font-family: "Montserrat", sans-serif;
  }
  
  .container {
    background-color: #f4f6f9;
    min-height: 100vh;
    padding: 16px;
  }
  
  .modern-card {
    position: relative;
    background-color: #ffffff;
    border-radius: 16px;
    overflow: hidden;
    transition: box-shadow 0.3s ease;
    cursor: pointer;
    margin: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  .modern-card:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }
  
  .modern-card-background {
    background-image: url('@/assets/newbackground.jpg'); /* Static background image */
    background-size: cover;
    background-position: center;
    width: 100%;
    height: 120px;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    position: relative;
  }
  
  .modern-card-number {
    background-color: rgba(0, 0, 0, 0.6); /* Semi-transparent background */
    color: white;
    font-size: 1rem;
    font-weight: bold;
    padding: 4px 12px;
    border-radius: 8px;
    margin-bottom: 12px;
  }
  
  .modern-card-status {
    background-color: #ff3b47;
    color: white;
    text-align: center;
    padding: 4px 16px;
    font-size: 0.8rem;
    border-radius: 20px;
    margin: -16px auto 10px auto;
    height: 30px;
    position: relative;
    width: fit-content;
  }
  
  .modern-card-customer-name {
    font-weight: bold;
    font-size: 0.9rem;
    text-align: center;
    margin: 8px 0;
  }
  
  .v-btn {
    font-size: 0.8rem;
    text-transform: none;
  }
  
  .v-card-actions {
    padding: 0 16px 16px;
    display: flex;
    justify-content: center;
  }
  
  /* Custom styling for white search field */
  .white-search-field .v-input__control {
    background-color: white;
    border-radius: 4px;
  }
  
  .white-search-field .v-label {
    color: black;
  }
  
  .white-search-field .v-input__icon--prepend-inner .v-icon {
    color: black;
  }
  
  /* Additional Styles for Improved Appearance */
  .back-button {
    color: white;
    border-radius: 50%;
    transition: transform 0.3s ease-in-out;
  }
  
  .back-button:hover {
    transform: scale(1.1);
  }
  
  .back-button .v-icon {
    color: white;
  }
  
  .add-job-button {
    font-family: "Montserrat", sans-serif;
    font-weight: 700;
  }
  
  .text-caption {
    font-size: 0.75rem;
    color: #757575;
  }
  
  .font-weight-medium {
    font-weight: 500;
  }
  
  /* Load More Button Styling */
  .load-more-button {
    font-weight: 600;
    margin-top: 16px;
    transition: background-color 0.3s ease, transform 0.3s ease;
  }
  
  .load-more-button:hover {
    background-color: #e63946;
    transform: scale(1.02);
  }
  
  /* Responsive Adjustments */
  @media (max-width: 600px) {
    .modern-card {
      margin: 8px;
      height: auto; /* Adjusted to auto for better responsiveness */
    }
    .v-col {
      margin-bottom: 16px;
    }
  }
  </style>
  